import { useEffect, useMemo, useState } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import { Icon } from '@antimatterhq/ui/components/icon';
import { DatasourceUpgradeStringFieldDiff } from '@/lib/datasource/upgrade/datasourceUpgrade.types';
import { DiffTextarea } from '@/components/diffTextarea/DiffTextarea';
import { exhaustiveMatchingGuard } from '@/utils/util';

export const DatasourceUpgradeDiffTextarea = ({
  diff,
  onApply,
  isApplying,
}: {
  diff: DatasourceUpgradeStringFieldDiff;
  onApply: (value: string) => void;
  isApplying?: boolean;
}) => {
  const [current, setCurrent] = useState(diff.upgradeValue ?? '');
  const [activeCurrentSource, setActiveCurrentSource] = useState<
    'upgradePreset' | 'currentPreset' | 'currentValue'
  >('currentValue');

  const previous = useMemo(() => {
    switch (activeCurrentSource) {
      case 'upgradePreset':
        return diff.upgradePresetValue ?? '';

      case 'currentPreset':
        return diff.currentPresetValue ?? '';

      case 'currentValue':
        return diff.currentValue ?? '';

      default:
        return exhaustiveMatchingGuard(activeCurrentSource);
    }
  }, [
    activeCurrentSource,
    diff.upgradePresetValue,
    diff.currentPresetValue,
    diff.currentValue,
  ]);

  useEffect(() => {
    setCurrent(diff.upgradeValue ?? '');
    setActiveCurrentSource('currentValue');
  }, [diff.upgradeValue]);

  const applied = useMemo(() => {
    return diff.upgradeValue === current && diff.status === 'confirmed';
  }, [diff.upgradeValue, current, diff.status]);

  return (
    <div
      className="flex w-full flex-col"
      data-testid={`datasource-upgrade-diff-textarea-root-${diff.title}`}
    >
      <div
        className="relative"
        data-testid="datasource-upgrade-diff-textarea-content"
      >
        <DiffTextarea
          current={current}
          previous={previous}
          onCurrentChange={(e) => setCurrent(e.target.value)}
          previousSource={activeCurrentSource}
          onChangePreviousSource={(source) => setActiveCurrentSource(source)}
        />
        {applied && (
          <div className="absolute right-4 top-3">
            <Icon
              icon="lucide:check"
              className="text-green-500"
              data-testid="datasource-upgrade-diff-textarea-accepted-icon"
            />
          </div>
        )}
      </div>
      <div className="flex w-full pt-4">
        <div className="flex-1 text-center">
          <Button
            onClick={() => setCurrent(previous)}
            rightIcon="lucide:chevron-right"
            disabled={current === previous}
            data-testid="datasource-upgrade-diff-textarea-copy-btn"
          >
            Copy to pending
          </Button>
        </div>
        <div className="flex-1 text-center">
          <Button
            onClick={() => onApply(current)}
            variant="primary"
            disabled={applied || isApplying}
            data-testid="datasource-upgrade-diff-textarea-apply-btn"
          >
            Apply pending version
          </Button>
        </div>
      </div>
    </div>
  );
};
